import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, Download, RefreshCw, Trophy } from 'lucide-react';
import { useCallback } from 'react';

import { useNarrativeState, usePlanningState } from '../store/hooks';
import { getWorldFromNodeId } from '../utils/narrativeHelpers';

interface CompletionScreenProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: () => void;
  onRestart: () => void;
}

export function CompletionScreen({ isOpen, onClose, onExport, onRestart }: CompletionScreenProps) {
  const narrativeState = useNarrativeState();
  const planningState = usePlanningState();

  // Calculate story statistics
  const getStoryStats = useCallback(() => {
    const totalNodes = Object.keys(narrativeState.nodes).length;
    const completedNodes = Object.values(narrativeState.nodes).filter(
      (node) => node.completionStatus === 'complete'
    ).length;

    const totalChoices = Object.values(narrativeState.nodes).reduce((sum, node) => {
      const standardChoices = node.choices?.length || 0;
      const goodWorldChoices = node.choicesGoodWorld?.length || 0;
      const badWorldChoices = node.choicesBadWorld?.length || 0;
      return sum + standardChoices + goodWorldChoices + badWorldChoices;
    }, 0);

    const worldPath =
      getWorldFromNodeId(narrativeState.currentNodeId) === 'bad' ? 'Worse World' : 'Better World';

    return {
      totalNodes,
      completedNodes,
      totalChoices,
      worldPath,
      playerName: planningState.playerName || 'Player',
      npcName: planningState.npcName || 'Character',
    };
  }, [narrativeState, planningState]);

  const stats = getStoryStats();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-gradient-to-b from-gray-900 to-gray-950 border border-gray-800 rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Celebration Header */}
          <div className="relative bg-gradient-to-r from-green-900/20 to-blue-900/20 p-8 text-center border-b border-gray-800">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', damping: 15 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full mb-4"
            >
              <Trophy className="w-10 h-10 text-green-400" />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-3xl font-bold text-white mb-2"
            >
              Congratulations!
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-lg text-gray-300"
            >
              {`You've completed your interactive story`}
            </motion.p>
          </div>

          {/* Story Stats */}
          <div className="p-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="grid grid-cols-2 gap-4 mb-8"
            >
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <div className="text-2xl font-bold text-white">{stats.completedNodes}</div>
                <div className="text-sm text-gray-400">Scenes Written</div>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <div className="text-2xl font-bold text-white">{stats.totalChoices}</div>
                <div className="text-sm text-gray-400">Player Choices</div>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <div className="text-xl font-bold text-white">
                  {stats.playerName} & {stats.npcName}
                </div>
                <div className="text-sm text-gray-400">Main Characters</div>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <div className="text-xl font-bold text-white">{stats.worldPath}</div>
                <div className="text-sm text-gray-400">Story Path</div>
              </div>
            </motion.div>

            {/* Success Message */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-green-900/20 border border-green-700 rounded-lg p-4 mb-8"
            >
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-green-300 mb-1">Story Complete!</h3>
                  <p className="text-sm text-green-200/80">
                    Your interactive narrative is ready to share. Export it as a PDF for easy
                    reading, or download the markdown version for further editing.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-3"
            >
              <button
                onClick={onExport}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
              >
                <Download className="w-5 h-5" />
                Export Story
              </button>

              <button
                onClick={onRestart}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
                Start New Story
              </button>
            </motion.div>

            {/* Continue Editing Option */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="text-center mt-6"
            >
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Continue editing this story
              </button>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
